import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trophy, Award, Send, Image as ImageIcon, Type, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { userApi, apiError, isParentView } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { ParentViewBar } from "../components/ParentViewBar";

export default function GraduacionUser() {
  const navigate = useNavigate();
  const readOnly = isParentView();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [mode, setMode] = useState("texto");
  const [comentario, setComentario] = useState("");
  const [imagen, setImagen] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => userApi.graduacion().then(setData).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openSubmit = (act) => { setActive(act); setMode("texto"); setComentario(""); setImagen(""); };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) { toast.error("La imagen debe pesar menos de 1.5 MB."); return; }
    const r = new FileReader();
    r.onload = () => setImagen(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    if (mode === "texto" && comentario.trim().length < 3) { toast.error("Escribe tu evidencia."); return; }
    if (mode === "imagen" && !imagen) { toast.error("Sube una imagen."); return; }
    setSending(true);
    try {
      const res = await userApi.submitActividadEvidencia(active.id, mode === "texto" ? comentario : comentario, mode === "imagen" ? imagen : "");
      toast.success(res.message || "Evidencia enviada y en proceso de revisión.");
      setActive(null);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };

  const pct = data ? Math.min(100, Math.round((data.puntos / (data.min_graduacion || 1)) * 100)) : 0;

  return (
    <div className="min-h-screen bg-cream">
      <header className="bg-navy">
        <div className="max-w-5xl mx-auto px-6 h-20 flex items-center justify-between">
          <button onClick={() => navigate("/mi-cuenta")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm font-semibold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Mi cuenta
          </button>
          <img src="/logo-supera-light.png" alt="Supera" className="h-11 w-auto object-contain" />
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <ParentViewBar />
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 rounded-xl bg-[var(--gold)] flex items-center justify-center">
            <Trophy className="w-7 h-7 text-[#0A1C30]" />
          </span>
          <div>
            <p className="text-xs font-extrabold tracking-[0.25em] text-[#B99B2E]">PUNTOS GRADUACIÓN SUPERA</p>
            <h1 className="text-3xl font-extrabold text-[#0A1C30]">Tu camino a la graduación</h1>
          </div>
        </div>

        {/* Progress */}
        {data && (
          <div className="mt-6 bg-navy rounded-3xl p-6 md:p-8 text-white">
            <div className="flex items-end justify-between flex-wrap gap-3">
              <div>
                <p className="text-white/60 text-sm">Puntos acumulados</p>
                <p className="text-5xl font-extrabold text-gold">{data.puntos}</p>
              </div>
              <p className="text-white/70 text-sm">Mínimo para graduarse: <span className="font-extrabold text-white">{data.min_graduacion}</span> · Excelencia: {data.max_excelencia}</p>
            </div>
            <div className="mt-4 h-3 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full rounded-full bg-[var(--gold)] transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-sm text-white/60">{pct}% de tu meta de graduación</p>
            <button onClick={() => navigate("/ranking")} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 text-sm transition-colors">
              <Award className="w-4 h-4 text-gold" /> Ver ranking general
            </button>
          </div>
        )}

        {/* Recognitions */}
        {data && (
          <div className="mt-8">
            <h2 className="text-xl font-extrabold text-[#0A1C30] mb-4">Reconocimientos</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {data.reconocimientos.map((m) => (
                <div key={m.id} className={`rounded-2xl p-4 text-center ring-1 ${m.ganado ? "bg-white ring-[var(--gold)]" : "bg-[#F0F1F4] ring-black/5 opacity-70"}`}>
                  <span className="mx-auto w-12 h-12 rounded-full flex items-center justify-center" style={{ background: m.ganado ? (m.color || "#F5C518") : "#D5D9DF" }}>
                    {m.ganado ? <Trophy className="w-6 h-6 text-white" /> : <Lock className="w-5 h-5 text-white" />}
                  </span>
                  <p className="mt-2 text-sm font-extrabold text-[#0A1C30] leading-tight">{m.nombre}</p>
                  <p className="text-xs text-[#6B7688]">{m.puntos} pts</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Activities */}
        <div className="mt-8">
          <h2 className="text-xl font-extrabold text-[#0A1C30] mb-4">Actividades y sus puntos</h2>
          <div className="space-y-3">
            {data?.actividades.map((a) => {
              const done = data.conteo_validadas[a.id] || 0;
              return (
                <div key={a.id} className="bg-white rounded-2xl ring-1 ring-black/5 p-5 flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-extrabold text-[#0A1C30]">{a.nombre}</p>
                    <p className="text-sm text-[#6B7688]">
                      <span className="font-bold text-[#E9A400]">{a.puntos_unitarios} pts</span> por evidencia · meta {a.cantidad_minima} ({a.puntos_minimos} pts)
                      {done > 0 && <span className="ml-2 inline-flex items-center gap-1 text-[#22A860] font-bold"><CheckCircle2 className="w-4 h-4" /> {done} validada(s)</span>}
                    </p>
                  </div>
                  {!readOnly && (
                    <button onClick={() => openSubmit(a)} className="inline-flex items-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold px-4 py-2.5 text-sm transition-colors">
                      <Send className="w-4 h-4" /> Subir evidencia
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Submit dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-extrabold text-[#0A1C30]">Evidencia · {active?.nombre}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <p className="text-sm text-[#6B7688]">Al validarse tu evidencia sumarás <span className="font-extrabold text-[#E9A400]">{active?.puntos_unitarios} puntos</span>.</p>
            <div className="flex gap-2">
              {[["texto", "Texto", Type], ["imagen", "Imagen", ImageIcon]].map(([v, l, Ic]) => (
                <button key={v} onClick={() => setMode(v)} className={`flex-1 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-bold text-sm transition-all ${mode === v ? "bg-navy text-white" : "bg-[#F4F6F9] text-[#0A1C30] hover:bg-[#EDF0F4]"}`}>
                  <Ic className="w-4 h-4" /> {l}
                </button>
              ))}
            </div>
            {mode === "texto" ? (
              <div>
                <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Describe tu evidencia</Label>
                <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} className="w-full min-h-[120px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" placeholder="Cuéntanos qué hiciste..." />
              </div>
            ) : (
              <div>
                <Label className="text-sm font-bold text-[#0A1C30] mb-1.5 block">Sube tu imagen (biblioteca o computadora)</Label>
                <input type="file" accept="image/*" onChange={onFile} className="block w-full text-sm text-[#6B7688] file:mr-3 file:rounded-lg file:border-0 file:bg-navy file:text-white file:px-4 file:py-2 file:font-bold file:cursor-pointer" />
                {imagen && <img src={imagen} alt="preview" className="mt-3 max-h-48 rounded-xl object-contain bg-[#F4F6F9] w-full" />}
                <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} className="mt-3 w-full min-h-[70px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" placeholder="Comentario (opcional)" />
              </div>
            )}
            <button onClick={submit} disabled={sending} className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--gold)] hover:bg-[var(--gold-soft)] text-[#0A1C30] font-extrabold py-3 transition-colors disabled:opacity-60">
              <Send className="w-5 h-5" /> {sending ? "Enviando..." : "Enviar evidencia"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
